import { Pressable, StyleSheet, ScrollView, Text, TextInput, useColorScheme } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useState } from "react";
import { View } from "../components/Themed";
import { DatePickerModal } from 'react-native-paper-dates';
import { Picker } from '@react-native-picker/picker';
import { useQuery, gql, useMutation } from "@apollo/client";
import { useNavigation } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';

const INVOICES_QUERY = gql`
  query getInvoices($token: String!, $dateFrom: String, $dateTo: String) {
    invoices(token: $token, dateFrom: $dateFrom, dateTo: $dateTo) 
  }
`;

const INVOICE_METADATA_LIST_QUERY = gql`
  query getInvoiceMetadataList($token: String!, $dateFrom: String) {
    invoiceMetadataList(token: $token, dateFrom: $dateFrom) {
      id
      filename
      dateFolder
      supplier { name }
      invoiceNumber
      totalAmount
      currency
      items { id inventoryItemId description }
    }
  }
`;

const UPLOAD_INVOICE_MUTATION = gql`
  mutation uploadInvoice($token: String!, $files: [Upload]!, $date: String!) {
    uploadInvoice(token: $token, files: $files, date: $date) { id file }
  }
`;

const DELETE_INVOICE_MUTATION = gql`
  mutation deleteInvoice($token: String!, $id: ID!) {
    deleteInvoice(token: $token, id: $id)
  }
`;

const SUPPLIERS_QUERY = gql`
  query getSuppliers($token: String!) {
    suppliers(token: $token) { id name }
  }
`;

export default function InvoicesScreen() {
  const [token, setToken] = useState("");
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const targetMonth = now.getDate() >= 15 ? now.getMonth() : now.getMonth() - 1;
  const targetYear = targetMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
  const adjMonth = targetMonth < 0 ? 11 : targetMonth;
  const defaultFrom = new Date(targetYear, adjMonth, 1);
  const defaultTo = new Date(targetYear, adjMonth + 1, 0); // last day of that month
  const [dateFrom, setDateFrom] = useState(fmtDate(defaultFrom));
  const [dateTo, setDateTo] = useState(fmtDate(defaultTo));
  const [openRange, setOpenRange] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [openMonthPicker, setOpenMonthPicker] = useState(false);
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const navigation = useNavigation();

  const { loading, error, data, refetch } = useQuery(INVOICES_QUERY, {
    variables: { token, dateFrom, dateTo },
    skip: !token,
  });

  const { data: metadataListData } = useQuery(INVOICE_METADATA_LIST_QUERY, {
    variables: { token, dateFrom },
    skip: !token,
  });

  const { data: suppliersData } = useQuery(SUPPLIERS_QUERY, {
    variables: { token },
    skip: !token,
  });
  const suppliers = suppliersData?.suppliers || [];

  useEffect(() => {
    AsyncStorage.getItem("token").then(t => setToken(t)).catch(() => {});
  }, []);

  const [uploadInvoice] = useMutation(UPLOAD_INVOICE_MUTATION);
  const [deleteInvoice] = useMutation(DELETE_INVOICE_MUTATION);

  const pickDocument = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        alert('Permission to access media library is required!');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsEditing: false,
        quality: 1,
        allowsMultipleSelection: true,
        base64: true,
      });
      if (!result.canceled) {
        setSelectedFile(result.assets);
      }
    } catch (err) {
      console.error('Error picking document:', err);
      alert('Error selecting file');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }
    setUploading(true);
    try {
      const result = await uploadInvoice({
        variables: {
          token,
          files: selectedFile.map(f => ({
            uri: f.uri,
            type: f.type || 'application/pdf',
            name: f.fileName || `invoice_${Date.now()}.pdf`,
          })),
          date: uploadMonth,
        },
      });
      if (result.data) {
        setSelectedFile(null);
        refetch();
        alert('Invoice uploaded successfully!');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error uploading invoice');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await deleteInvoice({ variables: { token, id } });
    if (result.data?.deleteInvoice) {
      refetch();
    }
  };

  const getMetaForFile = (filename) => {
    const dateFolder = dateFrom.replace(/-/g, '').substring(0, 6);
    return (metadataListData?.invoiceMetadataList || []).find(
      m => m.filename === filename && m.dateFolder === dateFolder
    );
  };

  const filteredInvoices = (data?.invoices || []).filter((invoice) => {
    const meta = getMetaForFile(invoice);
    if (selectedSupplier) {
      const supplierName = meta?.supplier?.name || "";
      if (supplierName !== selectedSupplier) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const invoiceNumber = meta?.invoiceNumber?.toLowerCase() || "";
      if (!invoiceNumber.includes(q)) return false;
    }
    return true;
  });

  const textColor = isDark ? "#fff" : "#000";
  const cardBg = isDark ? "#1e1e1e" : "#fff";

  return (
    <ScrollView style={{ backgroundColor: isDark ? "#121212" : "#f0f0f0" }}>
      <View style={styles.container}>

        <View style={styles.uploadSection}>
          <Text style={[styles.sectionTitle, { color: textColor }]}>Upload Invoice</Text>
          <Pressable style={styles.button} onPress={() => setOpenMonthPicker(true)}>
            <Text>Upload Month: {uploadMonth}</Text>
          </Pressable>
          <DatePickerModal
            locale="en"
            mode="single"
            visible={openMonthPicker}
            onDismiss={() => setOpenMonthPicker(false)}
            date={new Date(uploadMonth + '-01')}
            onConfirm={(params) => {
              const selectedDate = params.date;
              const year = selectedDate.getFullYear();
              const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
              setUploadMonth(`${year}-${month}`);
              setOpenMonthPicker(false);
            }}
          />
          <Pressable style={styles.button} onPress={pickDocument}>
            <Text>Select Invoice File</Text>
          </Pressable>
          
          {selectedFile && (
            <View style={styles.selectedFile}>
              <Text>Selected {selectedFile.length} file/s</Text>
              <Pressable 
                style={[styles.button, styles.uploadButton]} 
                onPress={handleUpload}
                disabled={uploading}
              >
                <Text>{uploading ? 'Uploading...' : 'Upload Invoices'}</Text>
              </Pressable>
            </View>
          )}
        </View>

        <Text style={[styles.title, { color: textColor }]}>Invoices</Text>
        <Pressable style={styles.datePicker} onPress={() => setOpenRange(true)}>
          <Text style={{ color: textColor, fontSize: 14 }}>{dateFrom}  →  {dateTo}</Text>
        </Pressable>
        <DatePickerModal
          locale="en"
          mode="range"
          visible={openRange}
          onDismiss={() => setOpenRange(false)}
          startDate={new Date(dateFrom)}
          endDate={new Date(dateTo)}
          onConfirm={({ startDate, endDate }) => {
            if (startDate) setDateFrom(fmtDate(startDate));
            if (endDate) setDateTo(fmtDate(endDate));
            setOpenRange(false);
          }}
        />

        <View style={[styles.filterRow]}>
          <View style={[styles.supplierPickerWrap, { backgroundColor: cardBg, borderColor: isDark ? '#333' : '#ddd' }]}>
            <Picker
              selectedValue={selectedSupplier}
              onValueChange={setSelectedSupplier}
              style={{ color: textColor }}
            >
              <Picker.Item label="All Suppliers" value="" />
              {suppliers.map(s => <Picker.Item key={s.id} label={s.name} value={s.name} />)}
            </Picker>
          </View>
          <TextInput
            style={[styles.searchInput, { flex: 1, color: textColor, backgroundColor: cardBg, borderColor: isDark ? '#333' : '#ddd' }]}
            placeholder="Invoice number..."
            placeholderTextColor={isDark ? '#888' : '#999'}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
            clearButtonMode="while-editing"
          />
        </View>
        {(selectedSupplier || searchQuery.trim() !== "") && (
          <Text style={[styles.searchResultCount, { color: isDark ? '#aaa' : '#666' }]}>
            {filteredInvoices.length} of {data?.invoices?.length || 0} invoices
          </Text>
        )}

        {loading && <Text style={{ color: textColor }}>Loading...</Text>}
        {error && <Text style={{ color: "#dc3545" }}>Error: {error.message}</Text>}

        {filteredInvoices.map((invoice) => {
          const meta = getMetaForFile(invoice);
          const linkedCount = (meta?.items || []).filter(i => i.inventoryItemId).length;
          return (
            <Pressable
              key={invoice}
              style={[styles.invoiceCard, { backgroundColor: cardBg }]}
              onPress={() => navigation.navigate('InvoicePreview', { invoiceFile: invoice, dateFrom })}
            >
              <View style={styles.invoiceHeader}>
                <View style={styles.invoiceInfo}>
                  <Text style={[styles.invoiceFilename, { color: textColor }]} numberOfLines={1}>
                    {invoice}
                  </Text>
                  {meta && (
                    <View style={styles.metaSummary}>
                      {meta.supplier && (
                        <Text style={styles.metaTag}>{meta.supplier.name}</Text>
                      )}
                      {meta.invoiceNumber && (
                        <Text style={styles.metaTag}>#{meta.invoiceNumber}</Text>
                      )}
                      {meta.totalAmount != null && (
                        <Text style={styles.metaTag}>
                          {meta.totalAmount.toFixed(2)} {meta.currency || ""}
                        </Text>
                      )}
                      {meta.items?.length > 0 && (
                        <Text style={[styles.metaTag, linkedCount > 0 && styles.metaTagLinked]}>
                          {meta.items.length} item{meta.items.length !== 1 ? 's' : ''}
                          {linkedCount > 0 ? ` (${linkedCount} linked)` : ''}
                        </Text>
                      )}
                    </View>
                  )}
                </View>
                <View style={styles.invoiceActions}>
                  {meta ? (
                    <View style={[styles.statusDot, { backgroundColor: "#28a745" }]} />
                  ) : (
                    <View style={[styles.statusDot, { backgroundColor: "#ccc" }]} />
                  )}
                  <Pressable
                    onPress={(e) => { e.stopPropagation(); handleDelete(invoice); }}
                    style={styles.deleteButton}
                  >
                    <Text style={styles.deleteText}>Delete</Text>
                  </Pressable>
                </View>
              </View>
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20 },
  datePicker: {
    backgroundColor: '#007AFF22', borderRadius: 8, padding: 12, marginBottom: 12,
    alignItems: 'center',
  },
  filterRow: {
    flexDirection: 'row', gap: 8, marginBottom: 6,
  },
  supplierPickerWrap: {
    flex: 1, borderRadius: 10, borderWidth: 1, justifyContent: 'center',
  },
  searchInput: {
    fontSize: 15, padding: 12, borderRadius: 10, borderWidth: 1,
  },
  searchResultCount: {
    fontSize: 12, marginBottom: 12, paddingLeft: 4,
  },
  button: { backgroundColor: '#007AFF', padding: 10, borderRadius: 5, marginBottom: 20 },
  selectedFile: { backgroundColor: '#f0f0f0', padding: 15, borderRadius: 5, marginBottom: 20 },
  uploadButton: { backgroundColor: '#28a745', marginTop: 10 },
  uploadSection: { marginTop: 30, marginBottom: 20 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 10 },
  invoiceCard: {
    borderRadius: 12, padding: 16, marginBottom: 12,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 2, elevation: 2,
  },
  invoiceHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
  },
  invoiceInfo: { flex: 1, paddingRight: 12 },
  invoiceFilename: { fontSize: 14, fontWeight: '500' },
  invoiceActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  statusDot: { width: 10, height: 10, borderRadius: 5 },
  deleteButton: {
    backgroundColor: '#dc3545', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6,
  },
  deleteText: { color: 'white', fontWeight: '600', fontSize: 12 },
  metaSummary: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  metaTag: {
    fontSize: 11, color: '#666', backgroundColor: '#f0f0f0',
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4, overflow: 'hidden',
  },
  metaTagLinked: { backgroundColor: '#d4edda', color: '#155724' },
});
